import { Paginator } from 'array-paginator';
import { defineStore } from 'pinia';
import { ResolveListForExportCallback } from 'src/stores/_infrastructure/ResolveListForExportCallback';
import { completeListExportViewFor } from 'src/stores/_infrastructure/completeListExportView';
import { resolveDesktopModuleDateFilterForQuery } from 'src/stores/_infrastructure/dateTimeRangeQueryValue';
import { dedupByAggregateId } from 'src/stores/_infrastructure/dedupByAggregateId';
import {
	COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
	prepareQueryForCompleteListExport,
} from 'src/stores/_infrastructure/prepareQueryForCompleteListExport';
import {
	DOCUMENT_LIST_GENERIC_ACTIVITY_LIST_SORT,
	DOCUMENT_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
} from 'src/stores/documentList/localStorageKeys';
import {
	GetTheDocumentModuleBoxesForTheUserQuery,
	GetTheDocumentModuleDocumentsForTheUserQuery,
	GetTheStandardDocumentModuleBoxForTheUserQuery,
	createDocumentListQueryForView,
} from 'src/stores/documentList/queries';
import createDocumentListState from 'src/stores/documentList/state';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { GetAllUsersWithAccessToTheModuleForTheUserFilterQuery } from 'src/stores/genericActivityList/queries';
import { TSMap } from 'typescript-map';

import { DateRangeSelection } from 'src/models/common';
import { DocumentListItem, DocumentListItemRunType } from 'src/models/documentList';
import { FilterBoxes, FilterBoxesRunType, FilterUsers, FilterUsersRunType } from 'src/models/filter';
import {
	ActivityListTypeEnum,
	GenericActivityListPagination,
	GenericActivityListSort,
} from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { ModuleNameEnum } from 'src/models/module/enums';
import { SharedQueryIdoDocumentBinding } from 'src/models/organization';
import { SerializedQuery } from 'src/models/query';
import { StatusEnum } from 'src/models/status/enums';

import {
	removeTheAggregateIdsFromTheCollection,
	setTheStateForTheAggregateIds,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { createCompareFunction } from 'src/util/collection/customCompareFunction';
import { DEFAULT_LIST_INSTANCE_ID, deriveListStoreId, isDefaultInstance } from 'src/util/listInstance/listInstanceKeys';

import {
	GetAllUsersWithAccessToTheModuleForTheUserFilterDocumentListCallBack,
	GetTheAllDocumentModuleDocumentsForTheUserCallBack,
	GetTheDocumentModuleBoxesForTheUserQueryCallBack,
	GetTheMineDocumentModuleDocumentsForTheUserCallBack,
	GetTheOpenDocumentModuleDocumentsForTheUserCallBack,
	GetTheStandardDocumentModuleBoxForTheUserCallBack,
} from './callbacks';
import {
	CompleteListExportFetchPayload,
	DeleteSelectedAggregateIdsPayload,
	ListFetchPayload,
	QueryStorePayload,
	SetDateFilterPayload,
	SetDateTimeRangeFilterPayload,
	SetFilteredBoxesPayload,
	SetFilteredUsersPayload,
	SetListPayload,
	SetListStatePayload,
	SetPaginationPagePayload,
	SetPaginationPayload,
	SetSelectedSharedQueryPayload,
	SetSortPayload,
} from './interfaces';

const DEFAULT_ROWS_PER_PAGE = 100;
const DEFAULT_LIST_SORT: GenericActivityListSort = {
	direction: 'DESC',
	property: 'timestamp',
};

const createDefaultPaginationEntry = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: DEFAULT_ROWS_PER_PAGE,
});

const createDefaultPaginationMap = (): TSMap<ActivityListTypeEnum, GenericActivityListPagination> =>
	new TSMap<ActivityListTypeEnum, GenericActivityListPagination>([
		[ActivityListTypeEnum.ALL, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.MINE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.OPEN, createDefaultPaginationEntry()],
	]);

const replaceActiveQueryStoreSubscription = <T>({
	matchesLogicalView,
	nextSerializedQuery,
	parseQuery,
	queryStore,
	subscriptions,
}: {
	matchesLogicalView: (serializedQuery: SerializedQuery) => boolean;
	nextSerializedQuery: SerializedQuery;
	parseQuery: (serializedQuery: SerializedQuery) => T;
	queryStore: ListFetchPayload['queryStore'];
	subscriptions: SerializedQuery[];
}): SerializedQuery[] => {
	const staleSubscriptions = subscriptions.filter(matchesLogicalView);

	staleSubscriptions.forEach((serializedQuery) => {
		void queryStore?.unsubscribeQuery(parseQuery(serializedQuery));
	});

	return [...subscriptions.filter((serializedQuery) => !matchesLogicalView(serializedQuery)), nextSerializedQuery];
};

// Task 3.1f: isolierte gespeicherte-Listen-Tabs (`$id !== 'documentList'`) sollen view-scoped Zähler/Zeilen zeigen —
// deshalb unterdrücken ihre Query-Konstruktionen die Box→Custom-Kopplung (sonst kollabieren Offen/Meine/Alle auf
// dieselbe Custom-Query → 8/8/8). Der Default-Store behält die klassische Kopplung (byte-identisch). Wird an ALLE
// Konstruktionsstellen (Dispatch + Register + Export) gereicht, damit Subscription-Identifier konsistent bleiben.
const suppressBoxCustomCouplingForInstance = (storeId: string): boolean => storeId !== 'documentList';

const defineDocumentListStore = (id: string) =>
	defineStore(id, {
		actions: {
			addQueryStoreSubscription(serializedQuery: SerializedQuery): void {
				this.queryStoreSubscriptions.push(serializedQuery);
			},
			clearQueryStoreSubscriptions(): void {
				this.queryStoreSubscriptions = [];
			},
			clearSubscriptions(payload: QueryStorePayload): void {
				if (payload.queryStore !== undefined) {
					this.queryStoreSubscriptions.forEach((serializedQuery: SerializedQuery) => {
						switch (serializedQuery.type) {
							case 'GetTheDocumentModuleDocumentsForTheUserQuery':
								void payload.queryStore?.unsubscribeQuery(
									GetTheDocumentModuleDocumentsForTheUserQuery.fromJSON<GetTheDocumentModuleDocumentsForTheUserQuery>(
										serializedQuery.query
									)
								);
								break;
							default:
								break;
						}
					});
				}

				this.clearQueryStoreSubscriptions();
			},
			deleteSelectedAggregateIds(payload: DeleteSelectedAggregateIdsPayload): void {
				const repaginateAndSet = (
					documentList: DocumentListItem[],
					totalCount: number,
					applyPage: (pagePayload: SetListPayload) => void
				): void => {
					const documentListCopy = removeTheAggregateIdsFromTheCollection(
						documentList as unknown as never[],
						payload.aggregateIds
					) as unknown as DocumentListItem[];

					if (documentListCopy !== documentList) {
						const documentListCopyPaginator = new Paginator(documentListCopy, DEFAULT_ROWS_PER_PAGE, 1);

						while (documentListCopyPaginator.current <= documentListCopyPaginator.total) {
							applyPage({
								page: documentListCopyPaginator.current,
								payload: documentListCopyPaginator.page(
									documentListCopyPaginator.current
								) as DocumentListItem[],
								totalCount: totalCount,
							});

							documentListCopyPaginator.next();
						}
					}
				};

				repaginateAndSet(this.documentListOpen, this.documentListOpenTotalCount, (listPayload) =>
					this.setOpenDocumentList(listPayload)
				);
				repaginateAndSet(this.documentListAll, this.documentListAllTotalCount, (listPayload) =>
					this.setAllDocumentList(listPayload)
				);
				repaginateAndSet(this.documentListMine, this.documentListMineTotalCount, (listPayload) =>
					this.setMineDocumentList(listPayload)
				);
			},
			async fetchAllDocumentList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setAllDocumentList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchAllDocumentList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.ALL);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const page = pagination.page;
				const query = createDocumentListQueryForView({
					binding: this.idoDocumentBinding,
					boxIdFilter: this.filteredBoxIds,
					date: resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					hiddenSearchTerm: payload.hiddenSearchTerm,
					pagination,
					searchTerm: payload.searchTerm,
					sort,
					suppressBoxCustomCoupling: suppressBoxCustomCouplingForInstance(this.$id),
					userIdFilter: this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					view: 'all',
				});
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.ALL, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.ALL, queryIdentifier);

				const callback = new GetTheAllDocumentModuleDocumentsForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<DocumentListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: DocumentListItemRunType,
				});
			},
			async fetchFilterBoxes(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterBoxes([]);
					return;
				}

				await payload.queryStore.query<Array<FilterBoxes>>({
					callback: new GetTheDocumentModuleBoxesForTheUserQueryCallBack(this),
					createTime: new Date(),
					query: new GetTheDocumentModuleBoxesForTheUserQuery(),
					responseType: 'json',
					runType: FilterBoxesRunType,
				});
			},
			async fetchFilterUsers(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterUsers([]);
					return;
				}

				await payload.queryStore.query<Array<FilterUsers>>({
					callback: new GetAllUsersWithAccessToTheModuleForTheUserFilterDocumentListCallBack(this),
					createTime: new Date(),
					query: new GetAllUsersWithAccessToTheModuleForTheUserFilterQuery(ModuleNameEnum.MODULE_DOCUMENT),
					responseType: 'json',
					runType: FilterUsersRunType,
				});
			},
			async fetchMineDocumentList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setMineDocumentList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchMineDocumentList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.MINE);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const page = pagination.page;
				const query = createDocumentListQueryForView({
					binding: this.idoDocumentBinding,
					boxIdFilter: this.filteredBoxIds,
					date: resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					hiddenSearchTerm: payload.hiddenSearchTerm,
					pagination,
					searchTerm: payload.searchTerm,
					sort,
					suppressBoxCustomCoupling: suppressBoxCustomCouplingForInstance(this.$id),
					userIdFilter: this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					view: 'mine',
				});
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.MINE, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.MINE, queryIdentifier);

				const callback = new GetTheMineDocumentModuleDocumentsForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<DocumentListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: DocumentListItemRunType,
				});
			},
			async fetchOpenDocumentList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setOpenDocumentList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchOpenDocumentList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.OPEN);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const page = pagination.page;
				const query = createDocumentListQueryForView({
					binding: this.idoDocumentBinding,
					boxIdFilter: this.filteredBoxIds,
					date: resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					hiddenSearchTerm: payload.hiddenSearchTerm,
					pagination,
					searchTerm: payload.searchTerm,
					sort,
					suppressBoxCustomCoupling: suppressBoxCustomCouplingForInstance(this.$id),
					userIdFilter: this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					view: 'open',
				});
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.OPEN, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.OPEN, queryIdentifier);

				const callback = new GetTheOpenDocumentModuleDocumentsForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<DocumentListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: DocumentListItemRunType,
				});
			},
			async fetchStandardBox(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setStandardBox(null);
					return;
				}

				await payload.queryStore.query<FilterBoxes>({
					callback: new GetTheStandardDocumentModuleBoxForTheUserCallBack(this),
					createTime: new Date(),
					query: new GetTheStandardDocumentModuleBoxForTheUserQuery(),
					responseType: 'json',
					runType: FilterBoxesRunType,
				});
			},
			async fetchTheCompleteListForExport(
				payload: CompleteListExportFetchPayload
			): Promise<DocumentListItem[] | null> {
				if (payload.queryStore === undefined) {
					return null;
				}

				const totalCountByListType: Partial<Record<ActivityListTypeEnum, number>> = {
					[ActivityListTypeEnum.ALL]: this.documentListAllTotalCount,
					[ActivityListTypeEnum.MINE]: this.documentListMineTotalCount,
					[ActivityListTypeEnum.OPEN]: this.documentListOpenTotalCount,
				};
				const pagination: GenericActivityListPagination = {
					page: 1,
					rowsPerPage:
						(totalCountByListType[payload.activityListType] ?? 0) + COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
				};
				const query = prepareQueryForCompleteListExport(
					createDocumentListQueryForView({
						binding: this.idoDocumentBinding,
						boxIdFilter: this.filteredBoxIds,
						date: resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
						hiddenSearchTerm: payload.hiddenSearchTerm,
						pagination,
						searchTerm: payload.searchTerm,
						sort: this.sortFor(payload.activityListType),
						suppressBoxCustomCoupling: suppressBoxCustomCouplingForInstance(this.$id),
						userIdFilter: this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
						view: completeListExportViewFor(payload.activityListType) as 'all' | 'mine' | 'open',
					})
				);

				let completeList: DocumentListItem[] | null = null;

				await payload.queryStore.query<DocumentListItem[]>({
					callback: new ResolveListForExportCallback<DocumentListItem>((list) => {
						completeList = list;
					}),
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: DocumentListItemRunType,
				});

				return completeList;
			},
			isLatestQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): boolean {
				return (this.activeQueryIdentifierByListType.get(activityListType) ?? '') === queryIdentifier;
			},
			registerUnsubscribeFetchAllDocumentList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const query = new GetTheDocumentModuleDocumentsForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					this.sortFor(ActivityListTypeEnum.ALL),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceDocumentListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchMineDocumentList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const query = new GetTheDocumentModuleDocumentsForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					this.sortFor(ActivityListTypeEnum.MINE),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceDocumentListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchOpenDocumentList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const query = new GetTheDocumentModuleDocumentsForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					this.sortFor(ActivityListTypeEnum.OPEN),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceDocumentListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			replaceDocumentListQueryStoreSubscription(payload: {
				query: GetTheDocumentModuleDocumentsForTheUserQuery;
				queryStore: ListFetchPayload['queryStore'];
			}): void {
				const nextSerializedQuery: SerializedQuery = {
					query: JSON.stringify(payload.query),
					type: 'GetTheDocumentModuleDocumentsForTheUserQuery',
				};
				const expectedStandardFilterSelection = payload.query.standardFilterSelection;

				this.queryStoreSubscriptions = replaceActiveQueryStoreSubscription({
					matchesLogicalView: (serializedQuery) => {
						if (serializedQuery.type !== nextSerializedQuery.type) {
							return false;
						}

						return (
							GetTheDocumentModuleDocumentsForTheUserQuery.fromJSON<GetTheDocumentModuleDocumentsForTheUserQuery>(
								serializedQuery.query
							).standardFilterSelection === expectedStandardFilterSelection
						);
					},
					nextSerializedQuery,
					parseQuery: (serializedQuery) =>
						GetTheDocumentModuleDocumentsForTheUserQuery.fromJSON<GetTheDocumentModuleDocumentsForTheUserQuery>(
							serializedQuery.query
						),
					queryStore: payload.queryStore,
					subscriptions: this.queryStoreSubscriptions,
				});
			},
			reset(payload: QueryStorePayload): void {
				this.clearSubscriptions({
					queryStore: payload.queryStore,
				});
				this.resetState();
			},
			resetState(): void {
				const today = new Date().toISOString().split('T')[0];
				this.activeQueryIdentifierByListType = new TSMap<ActivityListTypeEnum, string>([
					[ActivityListTypeEnum.ALL, ''],
					[ActivityListTypeEnum.MINE, ''],
					[ActivityListTypeEnum.OPEN, ''],
				]);
				this.dateFilter = today;
				this.today = today;
				this.dateTimeRangeFilter = PreconfiguredTimeRanges.TODAY;
				this.filteredUsers = [];
				this.filteredBoxIds = [];
				this.selectedSharedQueryId = '';
				this.idoDocumentBinding = null;
				this.queryStoreSubscriptions = [];
				this.documentListOpenTotalCount = 0;
				this.documentListOpen = [];
				this.documentListOpenSliced.clear();
				this.documentListAllTotalCount = 0;
				this.documentListAll = [];
				this.documentListAllSliced.clear();
				this.documentListMineTotalCount = 0;
				this.documentListMine = [];
				this.documentListMineSliced.clear();
				this.standardBox = null;
				this.pagination = createDefaultPaginationMap();
			},
			setActiveQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): void {
				this.activeQueryIdentifierByListType.set(activityListType, queryIdentifier);
			},
			setAllDocumentList(payload: SetListPayload): void {
				this.documentListAllTotalCount = payload.totalCount;
				this.documentListAllSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				this.documentListAll = dedupByAggregateId(
					[...this.documentListAllSliced.values()].flatMap((entry) => entry)
				).sort(
					createCompareFunction<DocumentListItem>(sort.property as keyof DocumentListItem, sort.direction)
				);
			},
			setDateFilterAction(payload: SetDateFilterPayload): void {
				this.setDateFilterValue(payload.dateFilter);
			},
			setDateFilterValue(payload: DateRangeSelection | string): void {
				this.dateFilter = payload || '';
			},
			setDateTimeRangeFilter(payload: SetDateTimeRangeFilterPayload): void {
				this.setDateTimeRangeFilterValue(payload.dateTimeRangeFilter);
			},
			setDateTimeRangeFilterValue(payload: PreconfiguredTimeRanges): void {
				this.dateTimeRangeFilter = payload;
			},
			setFilterBoxes(payload: Array<FilterBoxes>): void {
				this.filterBoxes = payload || [];
			},
			setFilterUsers(payload: Array<FilterUsers>): void {
				this.filterUsers = payload || [];
			},
			setFilteredBoxes(payload: Array<string>): void {
				this.filteredBoxIds = payload || [];
			},
			setFilteredBoxesAction(payload: SetFilteredBoxesPayload): void {
				this.setFilteredBoxes(payload.boxIdFilter);
			},
			setFilteredUsers(payload: Array<FilterUsers>): void {
				this.filteredUsers = payload || [];
			},
			setFilteredUsersAction(payload: SetFilteredUsersPayload): void {
				this.setFilteredUsers(payload.filterUsers);
			},
			setIdoDocumentBinding(binding: SharedQueryIdoDocumentBinding | null): void {
				this.idoDocumentBinding = binding;
			},
			setListLoadError(): void {
				const genericActivityListStore = useGenericActivityListStore();
				genericActivityListStore.setListLoadError();
			},
			setListViewModeLarge(payload: boolean): void {
				if (this.$id === 'documentList') {
					localStorage.setItem(DOCUMENT_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE, JSON.stringify(payload));
				}
				this.listViewModeLarge = payload;
			},
			setMineDocumentList(payload: SetListPayload): void {
				this.documentListMineTotalCount = payload.totalCount;
				this.documentListMineSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				this.documentListMine = dedupByAggregateId(
					[...this.documentListMineSliced.values()].flatMap((entry) => entry)
				).sort(
					createCompareFunction<DocumentListItem>(sort.property as keyof DocumentListItem, sort.direction)
				);
			},
			setOpenDocumentList(payload: SetListPayload): void {
				this.documentListOpenTotalCount = payload.totalCount;
				this.documentListOpenSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				this.documentListOpen = dedupByAggregateId(
					[...this.documentListOpenSliced.values()].flatMap((entry) => entry)
				).sort(
					createCompareFunction<DocumentListItem>(sort.property as keyof DocumentListItem, sort.direction)
				);
			},
			setPagination(payload: SetPaginationPayload): void {
				this.pagination.set(payload.activityListType, payload.pagination);
			},
			setPaginationAction(payload: SetPaginationPayload): void {
				this.setPagination(payload);
			},
			setPaginationPage(payload: SetPaginationPagePayload): void {
				const pagination = this.paginationFor(payload.activityListType);
				pagination.page = payload.page;
				this.pagination.set(payload.activityListType, pagination);
			},
			setPaginationPageAction(payload: SetPaginationPagePayload): void {
				this.setPaginationPage(payload);
			},
			setSelectedSharedQuery(payload: string): void {
				this.selectedSharedQueryId = payload || '';
			},
			setSelectedSharedQueryAction(payload: SetSelectedSharedQueryPayload): void {
				this.setSelectedSharedQuery(payload.selectedSharedQuery ?? '');
			},
			setSort(payload: SetSortPayload): void {
				this.sort.set(payload.activityListType, payload.sort);
			},
			setSortAction(payload: SetSortPayload): void {
				this.setSort(payload);
				if (this.$id === 'documentList') {
					localStorage.setItem(DOCUMENT_LIST_GENERIC_ACTIVITY_LIST_SORT, JSON.stringify(this.sort.toJSON()));
				}
			},
			setStandardBox(payload: FilterBoxes | null): void {
				this.standardBox = payload || null;
			},
			setTheDocumentStateToReady(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.READY);
			},
			setTheDocumentStateToSeen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.SEEN);
			},
			setTheDocumentStateToUnseen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.UNSEEN);
			},
			setTheStateForAggregateIds(aggregateIds: string[], status: StatusEnum): void {
				let openCountDelta = 0;
				aggregateIds.forEach((aggregateId) => {
					const known =
						this.documentListOpen.find((item) => item.aggregateId === aggregateId) ??
						this.documentListAll.find((item) => item.aggregateId === aggregateId) ??
						this.documentListMine.find((item) => item.aggregateId === aggregateId);
					if (!known) {
						return;
					}
					const wasReady = known.statusButton.status === StatusEnum.READY;
					const willBeReady = status === StatusEnum.READY;
					if (!wasReady && willBeReady) {
						openCountDelta -= 1;
					} else if (wasReady && !willBeReady) {
						openCountDelta += 1;
					}
				});
				if (openCountDelta !== 0) {
					this.documentListOpenTotalCount = Math.max(0, this.documentListOpenTotalCount + openCountDelta);
				}

				const setPagedList = <T>(
					entries: T[],
					totalCount: number,
					applyPage: (payload: { page: number; payload: T[]; totalCount: number }) => void
				): void => {
					if (entries.length === 0) {
						return;
					}

					const paginator = new Paginator(entries, DEFAULT_ROWS_PER_PAGE, 1);
					while (paginator.current <= paginator.total) {
						applyPage({
							page: paginator.current,
							payload: paginator.page(paginator.current) as T[],
							totalCount: totalCount,
						});
						paginator.next();
					}
				};

				setPagedList<DocumentListItem>(
					setTheStateForTheAggregateIds(
						this.documentListOpen as never[],
						aggregateIds,
						status
					) as unknown as DocumentListItem[],
					this.documentListOpenTotalCount,
					(payload) => this.setOpenDocumentList(payload)
				);
				setPagedList<DocumentListItem>(
					setTheStateForTheAggregateIds(
						this.documentListAll as never[],
						aggregateIds,
						status
					) as unknown as DocumentListItem[],
					this.documentListAllTotalCount,
					(payload) => this.setAllDocumentList(payload)
				);
				setPagedList<DocumentListItem>(
					setTheStateForTheAggregateIds(
						this.documentListMine as never[],
						aggregateIds,
						status
					) as unknown as DocumentListItem[],
					this.documentListMineTotalCount,
					(payload) => this.setMineDocumentList(payload)
				);
			},
			setToday(payload: string): void {
				this.today = payload || '';
			},
			silentSetAllDocumentList(): void {
				this.documentListAllTotalCount = 0;
				this.documentListAll = [];
				this.documentListAllSliced.clear();
				this.pagination.set(ActivityListTypeEnum.ALL, createDefaultPaginationEntry());
			},
			silentSetMineDocumentList(): void {
				this.documentListMineTotalCount = 0;
				this.documentListMine = [];
				this.documentListMineSliced.clear();
				this.pagination.set(ActivityListTypeEnum.MINE, createDefaultPaginationEntry());
			},
			silentSetOpenDocumentList(): void {
				this.documentListOpenTotalCount = 0;
				this.documentListOpen = [];
				this.documentListOpenSliced.clear();
				this.pagination.set(ActivityListTypeEnum.OPEN, createDefaultPaginationEntry());
			},
		},
		getters: {
			paginationFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListPagination {
				return (activityListType: ActivityListTypeEnum): GenericActivityListPagination =>
					state.pagination.get(activityListType) ?? createDefaultPaginationEntry();
			},
			sortFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListSort {
				return (activityListType: ActivityListTypeEnum): GenericActivityListSort =>
					state.sort.get(activityListType) ?? { ...DEFAULT_LIST_SORT };
			},
		},
		state: createDocumentListState,
	});

export const useDocumentListStore = defineDocumentListStore('documentList');

const documentListInstanceDefinitions = new Map<string, ReturnType<typeof defineDocumentListStore>>();

export const createDocumentListStore = (instanceId: string = DEFAULT_LIST_INSTANCE_ID) => {
	if (isDefaultInstance(instanceId)) {
		return useDocumentListStore();
	}

	const storeId = deriveListStoreId('documentList', instanceId);
	let definition = documentListInstanceDefinitions.get(storeId);

	if (definition === undefined) {
		definition = defineDocumentListStore(storeId);
		documentListInstanceDefinitions.set(storeId, definition);
	}

	return definition();
};

export * from './interfaces';
export * from './types';
